/**
 * SEOHead — applies per-route <title>, description, Open Graph and Twitter
 * Card meta at render time. Static fallbacks live in /app/frontend/public/index.html
 * so SPA crawlers (LinkedIn, WhatsApp, Telegram, Slack, X/Twitter) see proper
 * previews even before any JavaScript runs.
 *
 * The actual DOM-writing helpers live in seo/metaTags.js so this component
 * stays a thin orchestrator (low cyclomatic complexity, easy to read).
 */
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { applyTitle, applyOpenGraph, applyTwitter, applyCanonical } from "@/components/seo/metaTags";

const DEFAULT_OG_IMAGE =
  (process.env.REACT_APP_BACKEND_URL || "") + "/og-default.jpg?v=4";

const LOCALE_BY_LANG = { en: "en_US", hi: "hi_IN" };

function resolveCopy({ title, description, titleKey, descKey, fallback, t }) {
  return {
    title: title || (titleKey ? t(titleKey) : undefined) || fallback?.title,
    desc:  description || (descKey ? t(descKey) : undefined) || fallback?.desc,
  };
}

function resolveCanonical(path) {
  const origin = process.env.REACT_APP_BACKEND_URL || "";
  const fallback = typeof window !== "undefined"
    ? window.location.pathname + window.location.search
    : "/";
  return origin + (path || fallback);
}

export default function SEOHead({ title, description, titleKey, descKey, fallback, path, ogImage }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.resolvedLanguage;

  // Memo the small derived bundle so the effect doesn't churn on identity-
  // unstable parents passing fresh fallback objects every render.
  const bundle = useMemo(() => {
    const copy = resolveCopy({ title, description, titleKey, descKey, fallback, t });
    return {
      copy,
      canonical: resolveCanonical(path),
      image: ogImage || DEFAULT_OG_IMAGE,
      locale: LOCALE_BY_LANG[lang] || "en_US",
    };
  }, [title, description, titleKey, descKey, fallback, path, ogImage, lang, t]);

  useEffect(() => {
    document.documentElement.lang = (lang || "en").slice(0, 2);
    applyTitle(bundle.copy);
    applyCanonical(bundle.canonical);
    applyOpenGraph(bundle);
    applyTwitter(bundle);
  }, [bundle, lang]);

  return null;
}
